import { useEffect, useState } from "react";
import { getAdminFeatureFlags } from "../redux/companies/companiesCrud";
import { refactorUserManagement } from "./helperFunctions";

export type StagingTestActionType = "billingCycle" | "trial";

// Status labels (refactorUserManagement) each staging test action is offered for
export const STAGING_TEST_ACTION_STATUSES: Record<StagingTestActionType, string[]> = {
  // a paid subscription the renewal cron will renew: Monthly/Annual, Running, no running trial
  billingCycle: ["Monthly", "Monthly, Suspended", "Annual", "Annual, Suspended"],
  trial: ["Trial", "Trial, Suspended", "Monthly, Trial", "Annual, Trial"],
};

/**
 * Which staging test actions to offer for a company. Mirrors getEligibilityProblem in the backend
 * (server/helpers/staging-test-actions.js), which checks again on every request.
 */
export const getStagingTestActionAvailability = (subscription: any, isSuspended: boolean): Record<StagingTestActionType, boolean> => {
  if (!subscription?.currentPlan) return { billingCycle: false, trial: false };
  const label = refactorUserManagement(
    subscription.currentPlan.plan,
    subscription.currentPlan.status,
    isSuspended,
    subscription.userTrial?.isTrialCancelled,
    subscription.userTrial?.isTrialStarted,
    subscription.userTrial?.isTrialExpired,
  );
  return {
    billingCycle:
      STAGING_TEST_ACTION_STATUSES.billingCycle.includes(label) &&
      subscription.subscriptionInfo?.isSubscribed === true &&
      subscription.subscriptionInfo?.isUnsubscribed === false &&
      !!subscription.subscriptionEndedAt,
    trial: STAGING_TEST_ACTION_STATUSES.trial.includes(label) && !!subscription.userTrial?.isTrialStarted && !subscription.userTrial?.isTrialExpired,
  };
};

// Fetched once per page load and shared by every caller. Anything but an explicit true from the
// backend - including a failed request - keeps the staging test actions hidden.
let stagingTestActionsEnabledRequest: Promise<boolean> | null = null;
const fetchStagingTestActionsEnabled = () => {
  if (!stagingTestActionsEnabledRequest) {
    stagingTestActionsEnabledRequest = getAdminFeatureFlags()
      .then((res) => res?.data?.stagingTestActionsEnabled === true)
      .catch(() => {
        stagingTestActionsEnabledRequest = null;
        return false;
      });
  }
  return stagingTestActionsEnabledRequest;
};

export const useStagingTestActionsEnabled = () => {
  const [enabled, setEnabled] = useState(false);
  useEffect(() => {
    let active = true;
    fetchStagingTestActionsEnabled().then((value) => {
      if (active) setEnabled(value);
    });
    return () => {
      active = false;
    };
  }, []);
  return enabled;
};
